import * as Sentry from '@sentry/browser'
import { kAgentV2MatterApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, getTopLevelKeys } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { AgentV2ClientSurface, AgentV2ConversationListResponse, AgentV2ConversationListResponseSchema } from '@/agent-v2/store/schemas'
import { agentV2Request } from './agent-v2-request'

export async function getAgentV2ConversationList(
  matterId: string,
  hostSurface: AgentV2ClientSurface,
  options?: { limit?: number | null; createdByMe?: boolean; hasActivity?: boolean }
): Promise<AgentV2ConversationListResponse> {
  const query = new URLSearchParams()
  if (hostSurface !== AgentV2ClientSurface.WEB) query.set('origin_surface', hostSurface)
  // Requests default to 50; callers pass null when refreshing the full-history cache.
  if (options?.limit !== null) query.set('limit', String(options?.limit ?? 50))
  if (options?.createdByMe) query.set('created_by_me', 'true')
  if (options?.hasActivity !== undefined) query.set('has_activity', String(options.hasActivity))
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kAgentV2MatterApiBasePath}${matterId}/conversations${query.size ? `?${query}` : ''}`
  return agentV2Request({
    url,
    method: 'GET',
    sentryEndpoint: 'GET /api/v2/matter/{matter_id}/conversations',
    parseResponse: (data, { response, sentryTags }) => {
      const parsed = AgentV2ConversationListResponseSchema.safeParse(data)
      if (parsed.success) return parsed.data

      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(new Error('Agent v2 conversation list response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            matterId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid Agent v2 conversation list response shape', parsed.error.issues)
    },
  })
}
